import { useEffect, useState } from 'react';
import Card from '../../components/card/Card';
import P from '../../components/texts/p/P';
import Image from '../../components/image/Image';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import OverdueStatusModal from '../../components/modals/OverdueStatusModal';
import { Toast } from '../../components/toast/toast';
import { getLoanInstallments } from '../../services/GuaranteesService';
import { getInstallmentColumns, InstallmentData } from '../../utils/installmentColumns';
import { OverdueData } from '../../utils/overdueColumns';
import { ITableData } from '../../types/interfaces';

interface InstallmentPlanTabProps {
  loanId: string;
  loanData: any;
}

const InstallmentPlanTab: React.FC<InstallmentPlanTabProps> = ({ loanId, loanData }) => {
  const [installments, setInstallments] = useState<InstallmentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState<InstallmentData | null>(null);
  const [tableData, setTableData] = useState<ITableData>({
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchInstallments();
  }, [loanId]);

  const fetchInstallments = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getLoanInstallments(loanId);
      
      if (response.success && 'data' in response) {
        const installmentsData = response.data.content || response.data || [];
        
        // Enriquecer los datos de las cuotas con información del préstamo para el modal
        const enrichedInstallments = installmentsData.map((installment: InstallmentData) => ({
          ...installment,
          id: parseInt(loanId),
          id_global: loanData?.id_global || '',
          type: '1', // ID del tipo PRESTAMO
          application_product_line_id: loanData?.application_product_line_id || 0,
          days_overdue: installment.due_days || 0,
          creditor_name: loanData?.creditor || '',
          currency: loanData?.currency || '',
          cuit: loanData?.cuit || '',
          company_name: loanData?.company_name || '',
          monetization_amount: installment.amount,
          total_installments: installmentsData.length,
          rate_type_id: loanData?.rate_type_id,
          rate_percentage: loanData?.rate_percentage,
        }));

        setInstallments(enrichedInstallments);
        
        // Si la respuesta tiene paginación
        if (response.data.total_elements !== undefined) {
          setTableData({
            page: response.data.page || 0,
            size: response.data.size || 10,
            totalElements: response.data.total_elements,
            totalPages: response.data.total_pages || 1,
          });
        } else {
          // Si no hay paginación, mostrar todos los datos
          setTableData({
            page: 0,
            size: installmentsData.length,
            totalElements: installmentsData.length,
            totalPages: 1,
          });
        }
      } else {
        setError('No se encontraron cuotas para este préstamo');
      }
    } catch (err) {
      console.error('Error fetching installments:', err);
      setError('Error al cargar el plan de cuotas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (row: InstallmentData) => {
    setSelectedInstallment(row);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInstallment(null);
  };

  const handleModalSuccess = () => {
    fetchInstallments();
  };

  const columns = getInstallmentColumns({ handleOpenModal });

  if (loading) {
    return (
      <Card margin={{ top: '4' }}>
        <TableSkeleton rows={10} columns={columns.length} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        display='grid'
        justifyContent='center'
        margin={{ top: '11', bottom: '11' }}
        width='100%'
      >
        <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
          <Image name='folder_empty' />
        </Card>
        <P variant='primary' align='center' withMargin={false} weight='bold'>
          {error}
        </P>
      </Card>
    );
  }

  if (installments.length === 0) {
    return (
      <Card
        display='grid'
        justifyContent='center'
        margin={{ top: '11', bottom: '11' }}
        width='100%'
      >
        <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
          <Image name='folder_empty' />
        </Card>
        <P variant='primary' align='center' withMargin={false} weight='bold'>
          No se encontraron cuotas.
        </P>
        <P variant='primary' align='center'>
          Este préstamo no tiene un plan de cuotas definido.
        </P>
      </Card>
    );
  }

  return (
    <>
      <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
        <Table
          columns={columns}
          data={installments}
          tableData={tableData}
          handleChangePage={() => {}}
        />
      </Card>

      <OverdueStatusModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        overdueItem={selectedInstallment as OverdueData | null}
        onSuccess={handleModalSuccess}
      />
      
      <Toast id='overdue-toast' />
    </>
  );
};

export default InstallmentPlanTab;
